import React, { useMemo, useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Sparkles, ShieldCheck, Award, MapPin, X, ArrowRight,
  Phone, MessageCircle, BadgeCheck, Image as ImageIcon, Video,
} from 'lucide-react';
import { Button } from '../components/ui/button';
import useSeo from '../hooks/useSeo';
import { SiteHeader, SiteFooter } from './ServicesIndex';
import {
  BEFORE_AFTER, GALLERY_FILTERS, GALLERY_STATS,
} from '../data/galleryData';
import BeforeAfterSlider from '../components/BeforeAfterSlider';

const STATS_TILES = [
  { Icon: BadgeCheck, value: GALLERY_STATS.treatments, label: 'Successful Treatments' },
  { Icon: Award, value: GALLERY_STATS.satisfaction, label: 'Customer Satisfaction' },
  { Icon: MapPin, value: GALLERY_STATS.cities, label: 'Cities Served' },
  { Icon: ShieldCheck, value: GALLERY_STATS.experience, label: 'Years Experience' },
];

export default function GalleryImages({ onOpenCalculator }) {
  const [filter, setFilter] = useState('all');
  const [active, setActive] = useState(null);

  useSeo(
    'Before & After Gallery — Real Pest Control Results',
    'See real before/after results from EntoRid pest control treatments — termite, cockroach, bed bugs, rodents and mosquito control across Uttarakhand.'
  );

  const items = useMemo(
    () => filter === 'all' ? BEFORE_AFTER : BEFORE_AFTER.filter((b) => b.service === filter),
    [filter]
  );

  // Lock body scroll while lightbox is open
  useEffect(() => {
    if (active) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [active]);

  // ESC closes lightbox
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setActive(null); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [active]);

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: 'Poppins, Inter, sans-serif' }}>
      <SiteHeader onOpenCalculator={onOpenCalculator} />

      {/* Breadcrumb */}
      <div className="bg-slate-50 border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-3 text-xs sm:text-sm text-gray-500 overflow-x-auto whitespace-nowrap">
          <Link to="/" className="hover:text-green-700">Home</Link>
          <span className="mx-2">›</span>
          <span>Gallery</span>
          <span className="mx-2">›</span>
          <span className="text-gray-700 font-medium">Before & After Images</span>
        </div>
      </div>

      {/* Sub-tabs: Images / Videos */}
      <div className="border-b border-gray-100 bg-white sticky top-0 z-30 backdrop-blur supports-[backdrop-filter]:bg-white/80">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-2 py-3 overflow-x-auto" data-testid="gallery-tabs">
            <Link
              to="/gallery/images"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-emerald-600 text-white text-sm font-semibold shadow-sm whitespace-nowrap"
              data-testid="tab-images-active"
            >
              <ImageIcon size={15} /> Images
            </Link>
            <Link
              to="/gallery/videos"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-full bg-slate-100 text-slate-700 text-sm font-semibold hover:bg-slate-200 whitespace-nowrap"
              data-testid="tab-videos"
            >
              <Video size={15} /> Videos
            </Link>
          </div>
        </div>
      </div>

      {/* HERO — trust banner */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-slate-900 to-emerald-900 text-white">
        <div aria-hidden className="absolute -top-24 -right-20 w-72 h-72 rounded-full bg-emerald-500/20 blur-3xl" />
        <div aria-hidden className="absolute -bottom-24 -left-20 w-72 h-72 rounded-full bg-blue-500/15 blur-3xl" />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10 sm:py-16 lg:py-20">
          <span className="inline-flex items-center gap-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 px-3 py-1 text-[11px] sm:text-xs font-semibold">
            <Sparkles size={13} className="text-emerald-300" /> Real Results · Verified Photos
          </span>
          <h1 className="mt-4 text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight max-w-3xl" data-testid="gallery-images-title">
            Real Results from Our Services
          </h1>
          <p className="mt-3 text-sm sm:text-lg text-white/80 max-w-2xl leading-relaxed">
            Drag the slider on each card to see how EntoRid transforms infested
            spaces into clean, pest-free homes and businesses.
          </p>

          {/* Stats tiles */}
          <div className="mt-7 grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3" data-testid="gallery-stats">
            {STATS_TILES.map(({ Icon, value, label }) => (
              <div key={label} className="bg-white/8 backdrop-blur-sm border border-white/10 rounded-xl px-3 py-3 sm:px-4 sm:py-4 flex items-center gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-lg bg-emerald-500/20 text-emerald-300 flex items-center justify-center flex-shrink-0">
                  <Icon size={18} />
                </div>
                <div className="min-w-0">
                  <div className="text-base sm:text-xl font-extrabold leading-none">{value}</div>
                  <div className="text-[10px] sm:text-xs text-white/65 mt-1 leading-tight">{label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Filter chips */}
      <section className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div
            className="flex gap-2 overflow-x-auto pb-1 -mx-4 px-4 sm:mx-0 sm:px-0 scroll-smooth no-scrollbar"
            data-testid="gallery-filters"
          >
            {GALLERY_FILTERS.map((f) => (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                data-testid={`filter-${f.id}`}
                className={`flex-shrink-0 px-4 h-10 min-h-[44px] rounded-full text-sm font-semibold transition-all whitespace-nowrap ${
                  filter === f.id
                    ? 'bg-emerald-600 text-white shadow-md'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                }`}
              >
                {f.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Grid */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {items.length === 0 ? (
          <div className="text-center py-16 text-gray-500">No results match this filter yet.</div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6" data-testid="gallery-grid">
            {items.map((item) => (
              <article
                key={item.id}
                className="group bg-white rounded-2xl ring-1 ring-gray-100 shadow-sm hover:shadow-md transition-shadow overflow-hidden flex flex-col"
                data-testid={`gallery-card-${item.id}`}
              >
                <BeforeAfterSlider
                  beforeUrl={item.beforeUrl}
                  beforeUrlSmall={item.beforeUrlSmall}
                  afterUrl={item.afterUrl}
                  afterUrlSmall={item.afterUrlSmall}
                  alt={item.title}
                  testId={`slider-${item.id}`}
                />
                <div className="p-4 flex flex-col gap-2 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <span className={`inline-flex items-center gap-1 text-[10px] sm:text-[11px] font-bold uppercase tracking-wider px-2 py-1 rounded-full ${chipClass(item.service)}`}>
                      {labelOf(item.service)}
                    </span>
                    <span className="text-[10px] sm:text-[11px] text-gray-400 truncate flex items-center gap-1">
                      <MapPin size={10} /> {item.location}
                    </span>
                  </div>
                  <h3 className="text-sm sm:text-base font-bold text-gray-900 leading-snug line-clamp-2">{item.title}</h3>
                  <p className="text-xs sm:text-[13px] text-gray-600 leading-relaxed line-clamp-2 flex-1">{item.description}</p>
                  <button
                    type="button"
                    onClick={() => setActive(item)}
                    data-testid={`gallery-open-${item.id}`}
                    className="mt-2 inline-flex items-center justify-center gap-1.5 text-emerald-700 hover:text-emerald-800 font-semibold text-xs sm:text-sm self-start"
                  >
                    View full size <ArrowRight size={14} />
                  </button>
                </div>
              </article>
            ))}
          </div>
        )}

        {/* CTA */}
        <section className="mt-12 sm:mt-16 rounded-3xl bg-gradient-to-br from-emerald-600 via-emerald-700 to-blue-700 text-white p-6 sm:p-10 lg:p-14 relative overflow-hidden" data-testid="gallery-cta">
          <div aria-hidden className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-white/10 blur-3xl" />
          <div className="relative">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-bold leading-tight max-w-2xl">
              Get the Same Results for Your Home
            </h2>
            <p className="mt-3 text-sm sm:text-base text-white/85 max-w-2xl leading-relaxed">
              Same-day appointments · Government-approved chemicals · Free re-treatment warranty.
            </p>
            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onOpenCalculator}
                data-testid="gallery-book-now"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white text-emerald-700 hover:bg-emerald-50 font-bold shadow-lg"
              >
                Book Now <ArrowRight size={16} className="ml-1.5" />
              </Button>
              <Button
                onClick={onOpenCalculator}
                data-testid="gallery-free-inspection"
                className="h-12 min-h-[48px] px-6 rounded-full bg-emerald-500 hover:bg-emerald-400 text-white font-bold shadow-lg"
              >
                Get Free Inspection
              </Button>
              <a
                href="https://api.whatsapp.com/send?phone=919410110910"
                target="_blank"
                rel="noreferrer"
                data-testid="gallery-whatsapp"
                className="h-12 min-h-[48px] px-6 rounded-full bg-white/10 backdrop-blur border border-white/30 hover:bg-white/20 inline-flex items-center justify-center gap-2 font-bold transition"
              >
                <MessageCircle size={16} /> WhatsApp Us
              </a>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />

      {/* LIGHTBOX */}
      {active && (
        <div
          className="fixed inset-0 z-[80] bg-black/85 backdrop-blur-sm flex items-center justify-center p-3 sm:p-6 animate-in fade-in duration-200"
          onClick={() => setActive(null)}
          data-testid="gallery-lightbox"
        >
          <div
            className="bg-white w-full sm:max-w-4xl max-h-[92vh] sm:max-h-[88vh] rounded-3xl overflow-hidden flex flex-col animate-in slide-in-from-bottom-4 duration-300 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b">
              <div className="min-w-0 pr-3">
                <h3 className="text-sm sm:text-base font-bold text-gray-900 truncate">{active.title}</h3>
                <p className="text-[11px] sm:text-xs text-gray-500 truncate flex items-center gap-1"><MapPin size={11} /> {active.location}</p>
              </div>
              <button
                onClick={() => setActive(null)}
                aria-label="Close"
                data-testid="lightbox-close"
                className="w-10 h-10 rounded-full bg-slate-100 hover:bg-slate-200 active:scale-95 transition flex items-center justify-center flex-shrink-0"
              >
                <X size={20} />
              </button>
            </div>

            {/* Slider — bigger version */}
            <div className="overflow-y-auto" style={{ WebkitOverflowScrolling: 'touch' }}>
              <div className="bg-slate-50 p-3 sm:p-5">
                <BeforeAfterSlider
                  beforeUrl={active.beforeUrl}
                  beforeUrlSmall={active.beforeUrlSmall}
                  afterUrl={active.afterUrl}
                  afterUrlSmall={active.afterUrlSmall}
                  alt={active.title}
                  testId="lightbox-slider"
                />
                <p className="text-[11px] text-center text-gray-500 mt-3">Drag the green handle ← → to compare</p>
              </div>

              {/* Description */}
              <div className="px-4 sm:px-6 py-4 sm:py-5 space-y-3">
                <div className="flex flex-wrap items-center gap-2">
                  <span className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider px-2.5 py-1 rounded-full ${chipClass(active.service)}`}>
                    {labelOf(active.service)}
                  </span>
                  <span className="text-[11px] text-gray-500 inline-flex items-center gap-1"><MapPin size={11} /> {active.location}</span>
                </div>
                <p className="text-sm text-gray-700 leading-relaxed">{active.description}</p>
              </div>

              {/* Bottom CTA */}
              <div className="px-4 sm:px-6 pb-5 sm:pb-6 flex flex-col sm:flex-row gap-2">
                <Button
                  onClick={() => { setActive(null); onOpenCalculator?.(); }}
                  data-testid="lightbox-book"
                  className="flex-1 h-12 min-h-[48px] rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-semibold"
                >
                  Book Similar Service <ArrowRight size={16} className="ml-1.5" />
                </Button>
                <a
                  href="tel:+917819988010"
                  data-testid="lightbox-call"
                  className="flex-shrink-0 h-12 min-h-[48px] px-4 rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-800 font-semibold inline-flex items-center justify-center gap-2"
                >
                  <Phone size={16} /> Call
                </a>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function labelOf(service) {
  const map = {
    termite: 'Termite',
    cockroach: 'Cockroach',
    bedbug: 'Bed Bugs',
    rodent: 'Rodent',
    mosquito: 'Mosquito',
  };
  return map[service] || service;
}

function chipClass(service) {
  const map = {
    termite: 'bg-amber-100 text-amber-800',
    cockroach: 'bg-emerald-100 text-emerald-800',
    bedbug: 'bg-rose-100 text-rose-800',
    rodent: 'bg-slate-100 text-slate-800',
    mosquito: 'bg-sky-100 text-sky-800',
  };
  return map[service] || 'bg-slate-100 text-slate-800';
}
